<template>
	<div class="compare-selections">
		<!-- 日期选择下拉菜单 -->
		<el-dropdown ref="dateDropdownRef" trigger="click" @command="handleDateCommand" @visible-change="handleDateVisibleChange">
			<span class="compare-item">
				{{ currentDateLabel }}
				<SvgIcons name="down" class="arrow-icon" :class="{ 'is-rotated': isDateDropdownVisible }" />
			</span>
			<el-dropdown-menu slot="dropdown" class="compare-dropdown-menu">
				<el-dropdown-item
					v-for="item in dateRange"
					:key="item.value"
					:command="item.value"
					:class="{ 'is-selected': item.value === activeDate }"
				>
					{{ item.label }}
				</el-dropdown-item>
			</el-dropdown-menu>
		</el-dropdown>

		<!-- 周期选择下拉菜单 -->
		<el-dropdown ref="periodDropdownRef" trigger="click" @command="handlePeriodCommand" @visible-change="handlePeriodVisibleChange">
			<span class="compare-item">
				{{ currentPeriodLabel }}
				<SvgIcons name="down" class="arrow-icon" :class="{ 'is-rotated': isPeriodDropdownVisible }" />
			</span>
			<el-dropdown-menu slot="dropdown" class="compare-dropdown-menu">
				<el-dropdown-item
					v-for="item in getComparisonPeriod"
					:key="item.value"
					:command="item.value"
					:class="{ 'is-selected': item.value === activePeriod }"
				>
					{{ item.label }}
				</el-dropdown-item>
			</el-dropdown-menu>
		</el-dropdown>
	</div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, onMounted, onUnmounted } from 'vue';
import { useFinanceCommon } from '../useFinanceCommon';

const props = defineProps({
	activeDate: {
		type: Number,
		required: true
	},
	activePeriod: {
		type: String,
		required: true
	}
});

const emit = defineEmits(['update:activeDate', 'update:activePeriod', 'dateChange', 'periodChange']);

const { dateRange, getComparisonPeriod } = useFinanceCommon();

// 下拉菜单引用
const dateDropdownRef = ref(null);
const periodDropdownRef = ref(null);

// 下拉菜单显示状态
const isDateDropdownVisible = ref(false);
const isPeriodDropdownVisible = ref(false);

// 计算当前选中项的文本标签（用于显示在触发元素上）
const currentDateLabel = computed(() => {
	const item = dateRange.value.find(item => item.value === props.activeDate);
	return item ? item.label : '';
});

const currentPeriodLabel = computed(() => {
	const item = getComparisonPeriod.value.find(item => item.value === props.activePeriod);
	return item ? item.label : '';
});

// 处理日期选择命令
const handleDateCommand = command => {
	emit('update:activeDate', command);
	emit('dateChange', command);
};

// 处理周期选择命令
const handlePeriodCommand = command => {
	emit('update:activePeriod', command);
	emit('periodChange', command);
};

// 监听日期下拉菜单显示状态变化
const handleDateVisibleChange = visible => {
	isDateDropdownVisible.value = visible;
};

// 监听周期下拉菜单显示状态变化
const handlePeriodVisibleChange = visible => {
	isPeriodDropdownVisible.value = visible;
};

// 处理 touchmove 事件，收起下拉框
const handleTouchMove = () => {
	if (isDateDropdownVisible.value && dateDropdownRef.value) {
		dateDropdownRef.value.hide();
	}
	if (isPeriodDropdownVisible.value && periodDropdownRef.value) {
		periodDropdownRef.value.hide();
	}
};

// 组件挂载时添加 touchmove 监听
onMounted(() => {
	document.addEventListener('touchmove', handleTouchMove, { passive: true });
});

// 组件卸载时移除 touchmove 监听
onUnmounted(() => {
	document.removeEventListener('touchmove', handleTouchMove);
});
</script>

<style lang="less" scoped>
.compare-selections {
	display: flex;
	gap: 16px;
	// box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);

	.compare-item {
		position: relative;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 3px 8px;
		background: unset !important;
		background-color: unset !important;
		border-width: 0px !important;
		border-color: rgba(0, 0, 0, 0) !important;
		border-image: none !important;
		color: var(--surf-inverse-color) !important;
		cursor: pointer;
		font-size: 14px;
		line-height: 18px;

		// 0.5px 外围圆角边框（通过伪类+定位+缩放实现）
		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			border: 0.5px solid rgba(var(--surf-inverse-color-rgb), 0.4);
			border-radius: 4px;
			transform: scale(0.5);
			transform-origin: 0 0;
			width: 200%;
			height: 200%;
			pointer-events: none;
		}

		.arrow-icon {
			transition: transform 0.3s ease;
			flex-shrink: 0;
			color: var(--surf-inverse-color) !important;
			font-size: 16px;

			&.is-rotated {
				transform: rotate(180deg);
			}
		}
	}
}
</style>

<style lang="less">
// 全局样式：下拉菜单挂载到 body，需要使用全局样式
.compare-dropdown-menu {
	&.el-dropdown-menu {
		max-width: 120px !important;
		min-width: auto !important;
		padding: 4px !important;
		margin-top: 7px !important;

		background-color: var(--surf-secondary-color);
		border-radius: 2px;
		border: 1px solid rgba(var(--surf-primary-color-rgb), 0.3);
		box-shadow: 0 10px 20px 0 rgba(var(--surf-primary-color-rgb), 0.4);
	}

	.el-dropdown-menu__item {
		white-space: normal !important;
		word-wrap: break-word !important;
		padding: 4px 12px !important;
		font-size: 14px !important;
		line-height: 24px !important;
		color: rgba(var(--surf-inverse-color-rgb), 0.6) !important;

		&:hover {
			background-color: rgba(var(--surf-inverse-color-rgb), 0.1) !important;
			border-radius: 2px !important;
		}

		&.is-selected {
			background-color: rgba(var(--surf-inverse-color-rgb), 0.1) !important;
			border-radius: 2px !important;
			color: var(--surf-inverse-color) !important;
		}
	}

	.popper__arrow {
		display: none !important;
	}
}
</style>
